import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { MdOutlineAdd, MdOutlineDelete, MdOutlineEdit } from 'react-icons/md';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { useNavigate } from 'react-router';
import { t } from 'i18next';
import i18n from 'src/i18n';
import "./surveys.css"
import { fetchAllVendors, fetchDataApi, uploadExcelForRiskProfile, deleteSurveyApi, inviteVendorApi } from 'src/services/question';
import { Modal } from 'reactstrap';
import { toast } from 'react-toastify';
import { FcApproval, FcExport } from 'react-icons/fc';
import { TfiImport } from 'react-icons/tfi';
import { CiExport } from 'react-icons/ci';
import * as XLSX from 'xlsx';
import { getUserRolePermissions } from 'src/services/userServicer';
import LoadingScreen from '../../../components/Loading/LoadingScreen';

import {FaEnvelope} from 'react-icons/fa';

const SurvyQuestionTable = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [surveys, setSurveys] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [notAllowed, setNotAllowed] = useState(false);
    const [userPermissions, setUserPermissions] = useState<any>(null);

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [selectedPublicSurveyId, setSelectedPublicSurveyId] = useState<string | null>(null);
    const [vendorName, setVendorName] = useState('');
    const [vendorEmail, setVendorEmail] = useState('');
    const [showVendorSelection, setShowVendorSelection] = useState(false);
    const [vendors, setVendors] = useState<any[]>([]);
    const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
    const [currentAction, setCurrentAction] = useState<'assessment' | 'export' | null>(null);
    const [currentSurveyId, setCurrentSurveyId] = useState<string | null>(null);

    const handleInviteVendor = async () => {
        if (!selectedPublicSurveyId) return;

        try {
            const response = await inviteVendorApi({
                name: vendorName,
                email: vendorEmail,
                publicSurveyId: selectedPublicSurveyId
            });

            if (response.success) {
                toast.success("Vendor invited successfully");
                setIsInviteModalOpen(false);
                setVendorName('');
                setVendorEmail('');
                setSelectedPublicSurveyId(null);
                fetchVendors(); // Refresh vendors list
            }
        } catch (error) {
            toast.error("Failed to invite vendor");
        }
    };

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const confirmDelete = (id: string) => {
        setSelectedSurveyId(id);
        toggleModal();
    };

    const handleDelete = async () => {
        if (selectedSurveyId !== null) {
            try {
                await deleteSurveyApi(selectedSurveyId);
                setSurveys(prevSurveys => prevSurveys.filter(survey => survey.id !== selectedSurveyId));
                toggleModal();
                toast.success(t("surveys.Survey deleted successfully"));
            } catch (error: any) {
                toast.error(t("surveys.Failed to delete survey"));
            }
        }
    };

    const fetchSurveys = async () => {
        setLoading(true);
        try {
            const data = await fetchDataApi(`publicSurvey?perPage=${pageSize}&pageNo=${page}&query=${searchTerm}`);
            if (data.success) {
                setSurveys(data.data);
                setTotalPages(data.totalPages);
                setError(false);
            }
        } catch (error: any) {
            setError(true);
            setErrorMsg(error.message || "An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const data = await fetchAllVendors();
            if (data.success) {
                setVendors(data.data);
            }
        } catch (error: any) {
            toast.error("Failed to fetch vendors");
        }
    };

    const fetchUserRolePermissions = async () => {
        try {
            const permissions = await getUserRolePermissions();
            if (permissions.success) {
                const userPagePermission = permissions.data.permissions.find(
                    (permission: any) => permission.page === "survey"
                );
                if (userPagePermission) {
                    setUserPermissions(userPagePermission);
                    setNotAllowed(!userPagePermission.permissions.show);
                } else {
                    setNotAllowed(true);
                }
            } else {
                setNotAllowed(true);
            }
        } catch (error: any) {
            toast.error(t("permissions.Error_fetching_user_role_permissions"));
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchUserRolePermissions();
        fetchVendors();
    }, []);

    // Fetch surveys when pagination or search changes
    useEffect(() => {
        fetchSurveys();
    }, [page, pageSize, searchTerm]);

    const handleDownloadTemplate = () => {
        const rcsaData = [
            ["Survey Name", "Category", "Reference", "Question", "Bank Response", 
             "Question Type", "Options", "Quarter", "Year", "answerdby"],
            ["Risk Profile Q1 2024", "Category 1", "Reference 1", "Question 1", 
             "Least", "single_choice", "Least,Minimal,Moderate,Significant,Most", 
             "Q1", "2024", "User Name"]
        ];

        const workbook = XLSX.utils.book_new();
        const rcsaSheet = XLSX.utils.aoa_to_sheet(rcsaData);
        XLSX.utils.book_append_sheet(workbook, rcsaSheet, 'RCSA');
        XLSX.writeFile(workbook, "Inherent Risk Profile.xlsx");
    };

    const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                setLoading(true);
                const response = await uploadExcelForRiskProfile(file);
                toast.success(response.message || "Excel file imported successfully");
                fetchSurveys(); // Refresh surveys after import
            } catch (error) {
                toast.error("Error importing Excel file");
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="loading-screen" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
                <div><LoadingScreen /></div>
            </div>
        );
    }

    return (
        <div className="root">
            <Breadcrumb>
                <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
                <BreadcrumbItem active style={{ paddingRight: '10px' }}>
                    {t("surveys.Surveys")}
                </BreadcrumbItem>
            </Breadcrumb>

            <div className="d-flex justify-content-between align-items-center mb-lg">
                <h5 className="mb-lg">Surveys</h5>
                
                {userPermissions?.create && (
                    <div>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleImportExcel}
                            style={{ display: 'none' }}
                            id="import-excel"
                        />
                        <label htmlFor="import-excel" className="submit-button2 mr-2" style={{ width: '250px', textAlign: 'center' }}>
                            <TfiImport className="mr-2 mb-2 font-weight-bold" size={12} />
                            {t("Standards_Page.import_excel")}
                        </label>

                        <button 
                            className="submit-button mr-2" 
                            onClick={handleDownloadTemplate} 
                            style={{ width: '250px', padding: '5px' }}
                        >
                            <CiExport className="mr-2 font-weight-bold" size={16} />
                            {t("Standards_Page.download_excel_template")}
                        </button>
                    </div>
                )}
            </div>

            {!notAllowed ? (
                <>
                    <div className="container_info" style={{ padding: '40px 20px', backgroundColor: 'var(--widget-bg)' }}>
                        <div className="d-flex justify-content-between my-2">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t("surveys.search_placeholder") || ''}
                                className="search-input"
                            />
                            {userPermissions?.create && (
                                <button 
                                    className="submit-button4"
                                    onClick={() => navigate('/create-survey')}
                                >
                                    <MdOutlineAdd size={20} />
                                    {t("surveys.Create Survey")}
                                </button>
                            )}
                        </div>

                        <div className="responsive-table">
                            <table className="styled-table">
                                <thead className="table-dark">
                                    <tr>
                                        <th style={{ width: '30%' }}>{t("surveys.title")}</th>
                                        <th style={{ width: '40%' }}>{t("surveys.description")}</th>
                                        <th>{t("surveys.actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {surveys.length > 0 ? (
                                        surveys.map((item: any) => (
                                            <tr key={item.id}>
                                                <td>{i18n.language === 'ar' ? item.name_ar : item.name_en}</td>
                                                <td>{i18n.language === 'ar' ? item.description_ar : item.description_en}</td>
                                                <td>

                                                <button
                                                        className="action-button-info"
                                                        title='Invite Vendor'
                                                        onClick={() => {
                                                            setSelectedPublicSurveyId(item.id);
                                                            setIsInviteModalOpen(true);
                                                        }}
                                                    >
                                                        <FaEnvelope size={20} />
                                                    </button>


                                                    {/*userPermissions?.permissions?.create && (
                                                        <button
                                                            className="action-button-success"
                                                            title='assessment'
                                                            onClick={() => {
                                                                setCurrentSurveyId(item.id);
                                                                setCurrentAction('assessment');
                                                                setShowVendorSelection(true);
                                                            }}
                                                        >
                                                            
                                                        </button>
                                                    )*/}

                                                    <button
                                                        className="action-button-success"
                                                        title='validate'
                                                        onClick={() => {
                                                            setCurrentSurveyId(item.id);
                                                            setCurrentAction('export');
                                                            setShowVendorSelection(true);
                                                        }}
                                                    >
                                                        <FcApproval size={20} />
                                                    </button>

                                                   

                                                    {userPermissions?.permissions?.edit && (
                                                        <button
                                                            className="action-button"
                                                            title='edit'
                                                            onClick={() => navigate(`/updateSurvey/${item.id}`)}
                                                        >
                                                            <MdOutlineEdit size={20} />
                                                        </button>
                                                    )}

                                                    {userPermissions?.permissions?.delete && (
                                                        <button
                                                            className="action-button-danger"
                                                            title='delete'
                                                            onClick={() => confirmDelete(item.id)}
                                                        >
                                                            <MdOutlineDelete size={20} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center alert alert-warning">
                                                {t("surveys.No surveys found")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {surveys.length > 0 && (
                        <div className="pagination-controls mt-5">
                            <button
                                className="btn btn-warning btn-sm text-white"
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                            >
                                {"<<"}
                            </button>
                            <button
                                className="btn btn-warning btn-sm text-white"
                                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                disabled={page === 1}
                            >
                                {"<"}
                            </button>
                            <button
                                className="btn btn-warning btn-sm text-white"
                                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={page >= totalPages}
                            >
                                {">"}
                            </button>
                            <button
                                className="btn btn-warning btn-sm text-white"
                                onClick={() => setPage(totalPages)}
                                disabled={page >= totalPages}
                            >
                                {">>"}
                            </button>
                            <span>
                                {t("surveys.page")} {page} {t("surveys.to")} {totalPages}
                            </span>
                        </div>
                    )}
                </>
            ) : (
                <div className="container_info">
                    <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
                        <h5 style={{ width: '100%' }} className="text-center">
                            {t("permissions.not_allowed")}
                        </h5>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                className="p-5"
                isOpen={isModalOpen}
                toggle={toggleModal}
            >
                <div className="modal-header">
                    <h5 className="modal-title">{t("Confirm Deletion") || "Confirm Deletion"}</h5>
                    <button type="button" className="close" onClick={toggleModal}>
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    <p>
                        {t("surveys.Are you sure you want to delete this survey? This action cannot be undone.")}
                    </p>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-outline-secondary" onClick={toggleModal}>
                        {t("surveys.Cancel") || "Cancel"}
                    </button>
                    {userPermissions?.permissions?.delete && (
                        <button className="btn btn-danger" onClick={handleDelete}>
                            <i className="bi bi-trash-fill me-2"></i>
                            {t("surveys.Delete") || "Delete"}
                        </button>
                    )}
                </div>
            </Modal>

            {/* Invite Vendor Modal */}
            <Modal
                isOpen={isInviteModalOpen}
                toggle={() => setIsInviteModalOpen(false)}
                className="p-4"
            >
                <div className="modal-header">
                    <h5 className="modal-title">Invite New Vendor</h5>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setIsInviteModalOpen(false)}
                    >
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={vendorName}
                            onChange={(e) => setVendorName(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            className="form-control"
                            value={vendorEmail}
                            onChange={(e) => setVendorEmail(e.target.value)}
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setIsInviteModalOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleInviteVendor}
                        disabled={!vendorName || !vendorEmail}
                    >
                        Send Invite
                    </button>
                </div>
            </Modal>

            {/* Vendor Selection Modal */}
            <Modal
                isOpen={showVendorSelection}
                toggle={() => {
                    setShowVendorSelection(false);
                    setSelectedVendorId(null);
                }}
                className="p-4"
            >
                <div className="modal-header">
                    <h5 className="modal-title">Select Vendor</h5>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setShowVendorSelection(false)}
                    >
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Vendors</label>
                        <select
                            className="form-control p-2"
                            value={selectedVendorId || ''}
                            onChange={(e) => setSelectedVendorId(e.target.value)}
                        >
                            <option value="">Select a vendor</option>
                            {vendors.map((vendor) => (
                                <option key={vendor.id} value={vendor.id}>
                                    {vendor.name} ({vendor.email})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowVendorSelection(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            if (selectedVendorId && currentSurveyId && currentAction) {
                                const path = currentAction === 'assessment'
                                    ? `/assessment-survey/${currentSurveyId}?vendorId=${selectedVendorId}`
                                    : `/export-survey/${currentSurveyId}/${selectedVendorId}`;
                                navigate(path);
                                setShowVendorSelection(false);
                            }
                        }}
                        disabled={!selectedVendorId}
                    >
                        Continue
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default SurvyQuestionTable;